import { type EventType, formatMoney, type Locale, pickLocale } from '@bb/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import {
  useConnections,
  useDeleteEventType,
  useDuplicateEventType,
  useEventTypes,
} from '@/features/booking/api';
import { FirstEventSetup } from '@/features/booking/first-event';
import { btnPrimary, btnSmall } from '@/lib/ui';

export const Route = createFileRoute('/app/booking/')({
  component: EventTypesPage,
});

function EventTypesPage() {
  const { t } = useTranslation(['booking', 'common']);
  const { data: user } = useCurrentUser();
  const { data: eventTypes = [], isLoading } = useEventTypes();
  const { data: connections = [] } = useConnections();

  const hasWriteTarget = connections.some(
    (c) => c.writeCalendarExternalId && c.status === 'active',
  );

  /**
   * First run. `created` is held here rather than inside the panel because
   * creating the first event type is exactly what makes the list non-empty,
   * so an emptiness test alone would tear the panel down at the moment it has
   * something to show.
   */
  const [created, setCreated] = useState<EventType | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const setupOpen = !isLoading && !dismissed && (created !== null || eventTypes.length === 0);

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium">{t('booking:eventTypes.title')}</h2>
        <Link to="/app/booking/event-types/$id" params={{ id: 'new' }} className={btnPrimary}>
          {t('booking:eventTypes.new')}
        </Link>
      </div>

      {!hasWriteTarget && !isLoading && !setupOpen ? (
        <Link
          to="/app/booking/calendars"
          className="block rounded-lg border border-dashed border-neutral-300 p-4 text-sm text-neutral-600 hover:bg-neutral-50"
        >
          {t('booking:empty.noCalendar')}
        </Link>
      ) : null}
      {!user?.username ? (
        <Link
          to="/app/settings"
          className="block rounded-lg border border-dashed border-neutral-300 p-4 text-sm text-neutral-600 hover:bg-neutral-50"
        >
          {t('booking:eventTypes.needUsername')}
        </Link>
      ) : null}

      {isLoading ? <p className="text-sm text-neutral-500">{t('common:status.loading')}</p> : null}
      {setupOpen ? (
        <FirstEventSetup
          created={created}
          onCreated={setCreated}
          onDone={() => setDismissed(true)}
        />
      ) : null}

      {/*
        Not rendered while the first-run panel is up. Shown beside it, the list
        offered a Delete button for the very event type the panel was
        announcing, and using it left a live link on screen for something that
        no longer existed.
      */}
      {setupOpen ? null : (
        <ul className="space-y-2">
          {eventTypes.map((et) => (
            <EventTypeRow
              key={et.id}
              eventType={et}
              username={user?.username ?? null}
              locale={(user?.locale ?? 'en') as Locale}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EventTypeRow({
  eventType,
  username,
  locale,
}: {
  eventType: EventType;
  username: string | null;
  locale: Locale;
}) {
  const { t } = useTranslation(['booking', 'common']);
  const duplicate = useDuplicateEventType();
  const remove = useDeleteEventType();
  const [copied, setCopied] = useState(false);
  const url = username ? `${window.location.origin}/${username}/${eventType.slug}` : null;

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-neutral-200 bg-white px-4 py-3">
      <span
        className="h-8 w-1 shrink-0 rounded"
        style={{ backgroundColor: eventType.colour }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <Link
          to="/app/booking/event-types/$id"
          params={{ id: eventType.id }}
          className="flex min-h-11 items-center truncate text-sm font-medium hover:underline sm:min-h-0 sm:block"
        >
          {pickLocale(eventType.name, locale)}
        </Link>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-neutral-500">
          <span>
            {t('booking:eventTypes.units.minutesShort', { count: eventType.durationMinutes })}
          </span>
          <span>{t(`booking:eventTypes.location.${eventType.location.kind}`)}</span>
          <span>
            {eventType.price && eventType.price.amount > 0
              ? formatMoney(eventType.price, locale)
              : t('booking:eventTypes.fields.free')}
          </span>
          {!eventType.active ? (
            <span className="rounded bg-neutral-100 px-1.5 text-neutral-600">
              {t('booking:eventTypes.inactive')}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex w-full shrink-0 gap-1.5 sm:w-auto">
        {url && eventType.visibility !== 'invite_only' ? (
          // A hidden event type 404s on its public URL, so copying the link
          // would hand out a dead one. The button says why in place of its own
          // label, since a tooltip is no use on a phone.
          <button
            type="button"
            onClick={() => void copy()}
            disabled={!eventType.active}
            title={eventType.active ? undefined : t('booking:eventTypes.hiddenNoLinkWhy')}
            className={`${btnSmall} flex-1 sm:flex-none`}
          >
            {!eventType.active
              ? t('booking:eventTypes.hiddenNoLink')
              : copied
                ? t('booking:eventTypes.linkCopied')
                : t('booking:eventTypes.copyLink')}
          </button>
        ) : null}
        {/* The common reason to open this row: answer an enquiry with a link. */}
        <Link
          to="/app/booking/invites"
          search={{ eventTypeId: eventType.id }}
          className={`${btnSmall} flex-1 sm:flex-none`}
        >
          {t('booking:eventTypes.inviteLink')}
        </Link>
        <button
          type="button"
          onClick={() => duplicate.mutate(eventType.id)}
          disabled={duplicate.isPending}
          className={`${btnSmall} flex-1 sm:flex-none`}
        >
          {t('booking:eventTypes.duplicate')}
        </button>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t('booking:eventTypes.deleteConfirm'))) remove.mutate(eventType.id);
          }}
          disabled={remove.isPending}
          className={`${btnSmall} flex-1 text-red-700 sm:flex-none`}
        >
          {t('common:actions.delete')}
        </button>
      </div>
    </li>
  );
}
